// src/pages/ResetTwoFactorLink.jsx
// Opened from the "Reset two-factor authentication" email
// (…/portal/?reset-2fa=<token>). Asks before removing the authenticator,
// because email scanners sometimes open links on their own.
import { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { checkResetLink, confirmResetLink } from '../utils/api';
import { pathForTab, DEFAULT_TAB } from '../utils/tabRoutes';
import AuthShell, { A, AuthMessage } from '../components/AuthShell';

export function resetTokenFromLocation() {
  return new URLSearchParams(window.location.search).get('reset-2fa');
}

export default function ResetTwoFactorLink({ token }) {
  const { user, logout } = useAuth();
  // state: 'checking' | 'ready' | 'problem' | 'working' | 'done'
  const [state, setState]     = useState('checking');
  const [email, setEmail]     = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    checkResetLink(token)
      .then(r => {
        if (r.valid) { setEmail(r.email); setState('ready'); }
        else { setMessage(r.message); setState('problem'); }
      })
      .catch(e => { setMessage(e.message); setState('problem'); });
  }, [token]);

  async function confirm() {
    setState('working');
    try {
      await confirmResetLink(token);
      if (user) await logout().catch(() => {});
      setState('done');
    } catch (e) {
      setMessage(e.message);
      setState('problem');
    }
  }

  function goToSignIn() {
    // A full load, so the portal starts fresh without the link in the address
    window.location.assign(pathForTab(DEFAULT_TAB));
  }

  return (
    <AuthShell stack>
      <h1 style={S.title}>Reset two-factor authentication</h1>

      {state === 'checking' && <p style={S.text}>Checking your link…</p>}

      {(state === 'ready' || state === 'working') && (
        <>
          <p style={S.text}>
            This removes the authenticator app from the Fax Portal account <strong>{email}</strong>.
            Afterwards, sign in again and you'll be asked to set up a new authenticator.
          </p>
          <p style={S.note}>You'll be signed out on every device. We'll email you to confirm the reset.</p>
          <button onClick={confirm} disabled={state === 'working'} className="auth-btn">
            {state === 'working' ? 'Removing…' : 'Remove my authenticator'}
          </button>
          <div style={S.links}>
            <button onClick={goToSignIn} style={S.link} className="auth-link">Cancel, keep my authenticator</button>
          </div>
        </>
      )}

      {state === 'problem' && (
        <>
          <AuthMessage tone="error">{message}</AuthMessage>
          <button onClick={goToSignIn} className="auth-btn">Go to sign in</button>
        </>
      )}

      {state === 'done' && (
        <>
          <AuthMessage tone="success">
            Your old authenticator was removed. Sign in again and you'll be asked to set up a new one.
          </AuthMessage>
          <button onClick={goToSignIn} className="auth-btn">Go to sign in</button>
        </>
      )}
    </AuthShell>
  );
}


const S = {  // 3.40.0: the page and card come from AuthShell; the rest follows its look (A)
  title: { ...A.title, margin: 0 },
  text: { ...A.text, margin: 0 },
  note: { margin: 0, fontSize: 13, color: '#607D8B', lineHeight: 1.5 },
  links: { textAlign: 'center' },
  link: A.link,
};
